/**
 * AnchorPals - Main Application
 * Your Crew at Every Port
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Public Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Yacht Owner Pages
import OwnerDashboard from './pages/owner/Dashboard';
import CreateRequest from './pages/owner/CreateRequest';
import EditRequest from './pages/owner/EditRequest';
import MyRequests from './pages/owner/MyRequests';
import RequestDetails from './pages/owner/RequestDetails';
import MyYachts from './pages/owner/MyYachts';

// Helper Pages
import HelperDashboard from './pages/helper/Dashboard';
import AvailableRequests from './pages/helper/AvailableRequests';
import HelperRequestDetails from './pages/helper/RequestDetails';
import MyServices from './pages/helper/MyServices';


// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminAnalytics from './pages/admin/Analytics';
import AdminUsers from './pages/admin/Users';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminRequests from './pages/admin/Requests';
import AdminHelpers from './pages/admin/Helpers';
import AdminMessaging from './pages/admin/Messaging';

// Shared Pages
import ProfilePage from './pages/shared/ProfilePage';
import ChatPage from './pages/shared/ChatPage';
import ConversationPage from './pages/shared/ConversationPage';
import NotificationsPage from './pages/shared/NotificationsPage';
import RatingsPage from './pages/shared/RatingsPage';

// Components
import ToastContainer from './components/common/ToastContainer';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Protected Route wrapper
 */
function ProtectedRoute({ children, allowedTypes }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Redirect authenticated users based on their type
 */
function AuthRedirect({ children }) {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    const dashboardPath = user.user_type === 'admin' ? '/admin' : user.user_type === 'helper' ? '/helper' : '/owner';
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <AuthRedirect>
          <LandingPage />
        </AuthRedirect>
      } />

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        } />
        <Route path="/register" element={
          <AuthRedirect>
            <RegisterPage />
          </AuthRedirect>
        } />
      </Route>

      {/* Yacht Owner Routes */}
      <Route element={
        <ProtectedRoute allowedTypes={['yacht_owner']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/owner" element={<OwnerDashboard />} />
        <Route path="/owner/request/new" element={<CreateRequest />} />
        <Route path="/owner/requests" element={<MyRequests />} />
        <Route path="/owner/requests/:id" element={<RequestDetails />} />
        <Route path="/owner/requests/:id/edit" element={<EditRequest />} />
        <Route path="/owner/yachts" element={<MyYachts />} />
      </Route>

      {/* Helper Routes */}
      <Route element={
        <ProtectedRoute allowedTypes={['helper']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/helper" element={<HelperDashboard />} />
        <Route path="/helper/requests" element={<AvailableRequests />} />
        <Route path="/helper/requests/:id" element={<HelperRequestDetails />} />
        <Route path="/helper/services" element={<MyServices />} />
      </Route>

      {/* Admin Routes */}
      <Route element={
        <ProtectedRoute allowedTypes={['admin']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:id" element={<AdminUserDetail />} />
        <Route path="/admin/requests" element={<AdminRequests />} />
        <Route path="/admin/helpers" element={<AdminHelpers />} />
        <Route path="/admin/messaging" element={<AdminMessaging />} />
      </Route>

      {/* Shared Authenticated Routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/ratings" element={<RatingsPage />} />
        <Route path="/messages/:conversationId" element={<ConversationPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
          <Router>
            <AuthProvider>
              <NotificationProvider>
                <AppRoutes />
                <ToastContainer />
              </NotificationProvider>
            </AuthProvider>
          </Router>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
